import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../config/firebase";
import { RootState } from "../../../../setup/redux/Store";
import {
  ProjectList,
  OpportunitiesWidget,
  OnboardingHero,
} from "../../../../_start/partials/widgets";
import { VideoPlayerModal } from "../_modals/VideoPlayerModal";
import { OnboardingWizardModal } from "./OnboardingWizardModal";
import { ErrorBoundary, WidgetErrorFallback } from "../../../components/ErrorBoundary";

interface Props {
  userID: string;
  impersonateUID?: string;
  impersonateEmail?: string;
}

export const StartDashboardPage: React.FC<Props> = ({
  userID,
  impersonateUID,
  impersonateEmail,
}) => {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [hasProjects, setHasProjects] = useState(false);
  const [projectCount, setProjectCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const firstName = useSelector((state: RootState) => state.auth.user?.firstname ?? "");

  // Effective user ID — impersonated uid takes precedence when set
  const effectiveUID = impersonateUID ?? userID;
  const isImpersonating = !!impersonateUID;

  const handleProjectsLoaded = useCallback((count: number) => {
    setHasProjects(count > 0);
    setProjectCount(count);
  }, []);

  // Check if user has completed onboarding (skip in impersonation mode)
  useEffect(() => {
    if (!userID || isImpersonating) return;
    const checkOnboarding = async () => {
      try {
        const userRef = doc(db, "users", userID);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (!data.onboardingComplete) {
            setShowOnboarding(true);
          }
        } else {
          // New user whose doc hasn't been written yet — show wizard
          setShowOnboarding(true);
        }
      } catch {
        // Non-critical — skip wizard on error
      }
    };
    checkOnboarding();
  }, [userID, isImpersonating]);

  return (
    <>
      {/* Impersonation banner */}
      {isImpersonating && (
        <div
          style={{
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            borderRadius: 10,
            padding: "10px 16px",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <i className="bi bi-eye" style={{ fontSize: 16, color: "#92400E", flexShrink: 0 }}></i>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#92400E", flexGrow: 1 }}>
            Viewing projects as: <strong>{impersonateEmail}</strong> — read-only mode
          </span>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#F59E0B",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              padding: "4px 12px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Exit
          </button>
        </div>
      )}

      {/* begin::Onboarding Hero */}
      {!isImpersonating && (
        <OnboardingHero
          userID={userID}
          onHowItWorks={() => setShowVideo(true)}
          hasProjects={hasProjects}
          projectCount={projectCount}
          className="mb-5 mb-xxl-8"
        />
      )}
      {/* end::Onboarding Hero */}

      {/* begin::Row */}
      <div className="row g-0 g-xl-5 g-xxl-8">
        <div className="col-xl-6">
          <ErrorBoundary fallback={<WidgetErrorFallback />}>
            <ProjectList
              className="card-stretch mb-5 mb-xxl-8"
              userID={effectiveUID}
              onProjectsLoaded={handleProjectsLoaded}
              readOnly={isImpersonating}
            />
          </ErrorBoundary>
        </div>
        <div className="col-xl-6">
          <ErrorBoundary fallback={<WidgetErrorFallback />}>
            <OpportunitiesWidget
              className="card-stretch mb-5 mb-xxl-8"
              userID={effectiveUID}
            />
          </ErrorBoundary>
        </div>
      </div>
      {/* end::Row */}

      {/* begin::Modals */}
      <VideoPlayerModal show={showVideo} handleClose={() => setShowVideo(false)} />
      {!isImpersonating && (
        <OnboardingWizardModal
          show={showOnboarding}
          userID={userID}
          firstName={firstName}
          onComplete={() => setShowOnboarding(false)}
        />
      )}
      {/* end::Modals */}
    </>
  );
};
